import { rtdb } from "../firebase";
import { ref, get, onValue } from "firebase/database";

const RTDB_PATH = "/counts";

/**
 * Read all counts from Realtime Database (one-time read)
 * Returns object like { "FORM1.docx": 12, "FORM3.docx": 6, ... }
 */
export async function fetchCounts() {
  try {
    const snap = await get(ref(rtdb, RTDB_PATH));
    const raw = snap.exists() ? snap.val() : {};
    const result = {};
    // raw is expected to be { form1: { count: n }, form3: { count: n }, form7a: { count: n }, form8a: { count: n }, form12: { count: n }, ... }
    for (const [key, val] of Object.entries(raw || {})) {
      const count = val && typeof val === "object" ? val.count || 0 : val || 0;
      // map back to DOCX name: FORM{N}.docx or FORM{N}{A}.docx
      const m = key.match(/^form(\d+[a-z]?)/i);
      if (m) {
        result[`FORM${m[1].toUpperCase()}.docx`] = Number(count);
      } else {
        // fallback use key directly
        result[key] = Number(count);
      }
    }
    // ensure keys exist
    ["FORM1.docx", "FORM3.docx", "FORM5.docx", "FORM7A.docx", "FORM8A.docx", "FORM12.docx", "FORM13.docx", "FORM14.docx", "FORM15.docx"].forEach((f) => {
      if (result[f] == null) result[f] = 0;
    });
    return result;
  } catch (err) {
    console.warn("Could not fetch counts from RTDB:", err);
    return { "FORM1.docx": 0, "FORM3.docx": 0, "FORM5.docx": 0, "FORM7A.docx": 0, "FORM8A.docx": 0, "FORM12.docx": 0, "FORM13.docx": 0, "FORM14.docx": 0, "FORM15.docx": 0 };
  }
}

/**
 * Subscribe to Realtime Database counts. Callback receives same shaped object as fetchCounts
 * Returns unsubscribe function
 */
export function subscribeToCountsRealtime(callback) {
  const countsRef = ref(rtdb, RTDB_PATH);
  const listener = onValue(countsRef, (snap) => {
    const raw = snap.exists() ? snap.val() : {};
    const mapped = {};
    for (const [key, val] of Object.entries(raw || {})) {
      const count = val && typeof val === "object" ? val.count || 0 : val || 0;
      // Handle both numeric (form1) and alphanumeric (form7a, form8a)
      const m = key.match(/^form(\d+[a-z]?)/i);
      if (m) mapped[`FORM${m[1].toUpperCase()}.docx`] = Number(count);
      else mapped[key] = Number(count);
    }
    ["FORM1.docx", "FORM3.docx", "FORM5.docx", "FORM7A.docx", "FORM8A.docx", "FORM12.docx", "FORM13.docx", "FORM14.docx", "FORM15.docx"].forEach((f) => {
      if (mapped[f] == null) mapped[f] = 0;
    });
    callback(mapped);
  }, (err) => {
    console.error("RTDB onValue error:", err);
    callback({ "FORM1.docx": 0, "FORM3.docx": 0, "FORM5.docx": 0, "FORM7A.docx": 0, "FORM8A.docx": 0, "FORM12.docx": 0, "FORM13.docx": 0, "FORM14.docx": 0, "FORM15.docx": 0 });
  });
  // onValue returns an unsubscribe function when you call off(ref, 'value', listener)
  // but in modular SDK the onValue itself returns an unsubscribe function
  return listener;
}
